import type { AnchorHTMLAttributes, ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';

interface CommonProps {
  label: string;
  className?: string;
  stopPropagation?: boolean;
  children: ReactNode;
}

type ButtonProps = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'type'> & {
    href?: undefined;
    as?: 'button';
  };

type LinkProps = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children'> & {
    href: string;
    as: 'link';
    target?: string;
    rel?: string;
  };

type Props = ButtonProps | LinkProps;

export default function IconButton(props: Props) {
  const { label, className, stopPropagation = false, children } = props;
  const baseClass = `ui-icon-button ${className ?? ''}`.trim();

  if (props.as === 'link' && props.href) {
    const {
      href,
      onClick,
      target,
      rel,
      as: _as,
      label: _linkLabel,
      className: _linkClassName,
      children: _linkChildren,
      stopPropagation: _linkStopPropagation,
      ...anchorProps
    } = props as LinkProps;

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      if (stopPropagation) {
        event.stopPropagation();
      }
      onClick?.(event);
    };

    return (
      <a
        {...anchorProps}
        href={props.href}
        aria-label={props.label}
        target={target}
        rel={rel}
        onClick={handleClick}
        className={baseClass}
      >
        {children}
      </a>
    );
  }

  const {
    onClick,
    stopPropagation: _buttonStopPropagation,
    ...buttonProps
  } = props as Omit<ButtonProps, 'label' | 'className' | 'children' | 'as'>;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    onClick?.(event);
  };

  return (
    <button
      {...buttonProps}
      type="button"
      aria-label={label}
      onClick={handleClick}
      className={baseClass}
    >
      {children}
    </button>
  );
}
